// Extras Configuration
// Priority: Zuppin API → Admin Override → Mock Default

export const EXTRAS_CONFIG = {
  MEET_GREET: {
    id: 'MEET_GREET',
    name: 'Meet & Greet',
    description: 'Our driver will meet you at arrivals with a name sign',
    icon: 'UserCheck',
    defaultPrice: 0,        // FREE - was €15
    originalPrice: 15,      // Show original price with strikethrough
    currency: '€',
    isFreeGift: true,       // Flag for special gift styling
    isAutoIncluded: true,   // Automatically included in all bookings
    inputRequired: false,   // Flight number is optional by default
    inputType: 'text',
    inputLabel: 'Flight Number',
    inputPlaceholder: 'e.g., TK1234',
    inputHelp: 'Optional - helps us track your flight',
    maxQuantity: 1,
    vehicleCompatibility: ['ALL'], // Compatible with all vehicles
  },
  EXTRA_STOP: {
    id: 'EXTRA_STOP',
    name: 'Extra Stop',
    description: 'Add an additional stop along your route',
    icon: 'MapPin',
    defaultPrice: 15,
    currency: '€',
    inputRequired: true, // Address is required
    inputType: 'text',
    inputLabel: 'Stop Address',
    inputPlaceholder: 'Enter the address for your extra stop',
    inputHelp: 'Required when adding an extra stop',
    maxQuantity: 3, // Max 3 extra stops
    vehicleCompatibility: ['ALL'],
  },
  BABY_SEAT: {
    id: 'BABY_SEAT',
    name: 'Baby Seat',
    description: 'Child safety seat for infants (0-12 months)',
    icon: 'Baby',
    defaultPrice: 5,
    currency: '€',
    inputRequired: false,
    inputType: 'quantity',
    inputLabel: 'Number of seats',
    inputPlaceholder: '',
    inputHelp: 'Select how many baby seats you need',
    maxQuantity: 4, // Max 4 baby seats
    vehicleCompatibility: ['ALL'],
  },
  CHILD_SEAT: {
    id: 'CHILD_SEAT',
    name: 'Child Seat',
    description: 'Booster seat for children (3-12 years)',
    icon: 'Baby',
    defaultPrice: 5,
    currency: '€',
    inputRequired: false,
    inputType: 'quantity',
    inputLabel: 'Number of seats',
    inputPlaceholder: '',
    inputHelp: 'Select how many child seats you need',
    maxQuantity: 4,
    vehicleCompatibility: ['ALL'],
  },
  WATER_BOTTLES: {
    id: 'WATER_BOTTLES',
    name: 'Water Bottles',
    description: 'Complimentary cold water bottles for your journey',
    icon: 'Droplets',
    defaultPrice: 0,
    currency: '€',
    inputRequired: false,
    inputType: 'checkbox',
    inputLabel: '',
    inputPlaceholder: '',
    inputHelp: 'Free of charge',
    maxQuantity: 1,
    vehicleCompatibility: ['ALL'],
  },
};

// Get all extras as array
export const getAllExtras = () => Object.values(EXTRAS_CONFIG);

// Get only optional extras (not auto-included)
export const getOptionalExtras = () => getAllExtras().filter(e => !e.isAutoIncluded);

// Get auto-included extras (gifts)
export const getAutoIncludedExtras = () => getAllExtras().filter(e => e.isAutoIncluded);

// Get extra by ID
export const getExtraById = (id) => EXTRAS_CONFIG[id] || null;

// Filter extras by vehicle compatibility
// Now supports 'ALL' keyword for universal compatibility
export const getCompatibleExtras = (vehicleId) => {
  if (!vehicleId) return getAllExtras();
  
  const normalizedId = vehicleId.toUpperCase().replace(/\s+/g, '_');
  
  return getAllExtras().filter(extra => {
    // If vehicleCompatibility includes 'ALL', it's compatible with all vehicles
    if (extra.vehicleCompatibility.includes('ALL')) return true;
    
    // Check for exact match
    if (extra.vehicleCompatibility.includes(normalizedId)) return true;
    
    // Check for partial match (e.g., 'VAN' matches 'VIP_VAN', 'MINIVAN')
    return extra.vehicleCompatibility.some(compat => 
      normalizedId.includes(compat) || compat.includes(normalizedId)
    );
  });
};

// Get compatible optional extras (excluding auto-included)
export const getCompatibleOptionalExtras = (vehicleId) => {
  return getCompatibleExtras(vehicleId).filter(e => !e.isAutoIncluded);
};

// Get compatible auto-included extras
export const getCompatibleAutoIncludedExtras = (vehicleId) => {
  return getCompatibleExtras(vehicleId).filter(e => e.isAutoIncluded);
};

// Calculate total extras price (auto-included are always €0)
export const calculateExtrasTotal = (selectedExtras) => {
  return selectedExtras.reduce((total, extra) => {
    // Auto-included extras are free
    if (extra.isAutoIncluded || extra.isFreeGift) return total;
    
    const quantity = extra.quantity || 1;
    const price = extra.price || extra.defaultPrice;
    return total + (price * quantity);
  }, 0);
};

// Build extras data for API submission (includes auto-included)
export const buildExtrasDataForAPI = (selectedExtras, inputValues, quantities) => {
  // Get auto-included extras
  const autoIncluded = getAutoIncludedExtras().map(extra => ({
    id: extra.id,
    code: extra.id.toLowerCase(),
    name: extra.name,
    original_price: extra.originalPrice || extra.defaultPrice || 0,
    price: 0, // Free
    quantity: 1,
    inputValue: inputValues?.[extra.id] || '',
    is_included: true,
    is_gift: true,
    is_auto_included: true,
  }));
  
  // Get user-selected extras
  const userSelected = selectedExtras.map(extra => ({
    id: extra.id,
    code: extra.id.toLowerCase(),
    name: extra.name,
    original_price: extra.price || extra.defaultPrice || 0,
    price: extra.price || extra.defaultPrice || 0,
    quantity: quantities?.[extra.id] || 1,
    inputValue: inputValues?.[extra.id] || '',
    is_included: false,
    is_gift: false,
    is_auto_included: false,
  }));
  
  return [...autoIncluded, ...userSelected];
};

// Validate extra input
export const validateExtraInput = (extra, inputValue) => {
  if (!extra.inputRequired) return { valid: true };
  
  if (extra.inputType === 'text' && (!inputValue || inputValue.trim() === '')) {
    return { valid: false, message: `${extra.inputLabel} is required` };
  }
  
  if (extra.inputType === 'quantity' && (!inputValue || inputValue < 1)) {
    return { valid: false, message: 'Please select at least 1' };
  }
  
  return { valid: true };
};
